import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NbDialogRef, NbCardModule, NbSpinnerModule, NbButtonModule } from '@nebular/theme';
import { KPI } from '../../../@core/api'
import { I18nService, ScopeService, SettingsService, StyleService, WorkflowService, InvestmentsService } from '../../../@core/utils'
import { CurrencyPipe } from '../../pipes/currency.pipe';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Observable, firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-workflow-start-round-dialog',
    styleUrls: ['./workflow-start-round-dialog.component.scss'],
    templateUrl: './workflow-start-round-dialog.component.html',
    standalone: true,
    imports: [
    NbCardModule,
    NbSpinnerModule,
    FontAwesomeModule,
    FormsModule,
    NgClass,
    NbButtonModule,
    TranslateModule,
    CurrencyPipe,
],
})
export class WorkflowStartRoundDialogComponent implements OnInit {
  @Output() created: EventEmitter<KPI.WorkflowRoundCreation> = new EventEmitter<KPI.WorkflowRoundCreation>()

  // string to present the total number and amount of the proposed tags
  // for a given sites
  public proposedFromTagsDisplay: string = ''

  public sites: KPI.WorkflowSite[] = [];
  public selectedSites: KPI.Perimeter[] = [];
  public user: KPI.User = null;
  public roundName: string = '';
  public level = 1;
  public steps: KPI.WorkflowStep[] = [];
  public outSteps: KPI.WorkflowStep[] = [];
  public investmentTags: KPI.InvestmentReason[] = [];
  public selectedPlan: KPI.InvestmentPlan = null;
  public errorMessage = '';
  public currency: string = '€';
  public unselectedTags: number[] = [];
  public projectCode: string = '';
  public projectCodeRequired: boolean = false;

  // when creating a new round, we need to disable the create button
  // not to be duplicated
  public isCreating = false;
  
  private cachedUniqueInvestmentIds: Set<number> = new Set();
  private cachedInvestmentIdsReady: boolean = false;
  private cachedUniqueInvestmentBudgets: Map<number, number> = new Map(); 

  constructor(
    private workflowService: WorkflowService,
    private ref: NbDialogRef<WorkflowStartRoundDialogComponent>,
    private translate: TranslateService,
    private scopeService: ScopeService,
    private i18n: I18nService,
    public styleService: StyleService,
    private investmentsService: InvestmentsService,
    private settingsService: SettingsService,
  ) { }

  public ngOnInit(): void {
    this.roundName = this.getRoundName()

    this.currency = this.i18n.getCurrency();
    this.settingsService.workflowProjectCodeAtTheEndOfWF$.subscribe((value) => {
      this.projectCodeRequired = value;
    });
    this.scopeService.user$.subscribe((user) => {
      if (user) {
        this.user = user;
      }
    });
    this.workflowService.loadWorkflowSteps().subscribe(
      (steps: KPI.WorkflowStep[]) => {
        this.steps = steps
        for (const step of this.steps.concat([])) {
          if (step.is_ignored_by_default) {
            const event = { target: { value: -1 }}
            this.setStepMode(event, step)
          }
        }
      }
    );
  }

  public getStepMode(step): number {
    if (this.steps.map(elt => elt.id).indexOf(step.id) >= 0) {
      return step.no_go_step ? 1 : 0;
    } else {
      return -1;
    }
  }

  public setStepMode(event, step) {
    const target = event.originalTarget || event.target;
    const value = +(target.value);
    if (value === -1) {
      const stepIndex = this.steps.map(elt => elt.id).indexOf(step.id);
      if (stepIndex >= 0) {
        this.steps.splice(stepIndex, 1);
        for (let index = 0; index < this.steps.length; index++) {
          this.steps[index].order = index + 1;
        }
        this.outSteps.push(step);
      }
    } else {
      step.no_go_step = !!value;
      const stepIndex = this.outSteps.map(elt => elt.id).indexOf(step.id);
      if (stepIndex >= 0) {
        this.outSteps.splice(stepIndex, 1);
        if (this.steps.length) {
          step.order = this.steps[this.steps.length - 1].order + 1;
        } else {
          step.order = 1;
        }
        this.steps.push(step);
      }
    }
  }

  public nextLevel(event) {
    event.preventDefault()
    if (this.level === 2) {
      this.createRound();
    }
    if (this.level < 2) {
      this.fetchUniqueInvestmentIds().then(() => {
        this.proposedFromTagsDisplay = this.getProposedTotalDisplay();
        this.level += 1;
      });
    }
  }
  
  private async fetchUniqueInvestmentIds(): Promise<void> {
    const selectedTagIds = this.getSelectedTagIds();
    const selectedIds = this.selectedSites.map(elt => elt.id);
    const sites = this.sites.filter(
      (site) => selectedIds.includes(site.id),
    );
    
    this.cachedUniqueInvestmentIds.clear();
    this.cachedUniqueInvestmentBudgets.clear();
    this.cachedInvestmentIdsReady = false;
    
    if (selectedTagIds.length === 0 || sites.length === 0) {
      this.cachedInvestmentIdsReady = true;
      return;
    }
    
    try {
      const investmentToSite = new Map<number, KPI.WorkflowSite>();
      
      for (const site of sites) {
        if (!site.proposed_by_tag) {
          continue;
        }
        
        for (const tagId of selectedTagIds) {
          const tagData = site.proposed_by_tag[tagId] || site.proposed_by_tag[String(tagId)];
          if (!tagData) {
            continue;
          }
          
          const investmentIds: number[] = tagData.investment_ids && Array.isArray(tagData.investment_ids) 
            ? tagData.investment_ids 
            : [];
          
          investmentIds.forEach(id => {
            if (id != null) {
              this.cachedUniqueInvestmentIds.add(id);
              if (!investmentToSite.has(id)) {
                investmentToSite.set(id, site);
              }
            }
          });
        }
      }
      
      const uniqueInvestmentIdsArray = Array.from(this.cachedUniqueInvestmentIds);
      if (uniqueInvestmentIdsArray.length > 0) {
        try {
          const investments = await firstValueFrom(
            this.investmentsService.getInvestmentsByIds(uniqueInvestmentIdsArray)
          );
          
          for (const investment of investments) {
            if (!investment || !investment.id) {
              continue;
            }
            
            if (this.cachedUniqueInvestmentBudgets.has(investment.id)) {
              continue;
            }
            
            let statusId: number;
            if (typeof investment.status === 'object' && investment.status !== null && (investment.status as any).id) {
              statusId = (investment.status as any).id;
            } else if (typeof investment.status === 'number') {
              statusId = investment.status;
            } else {
              statusId = null;
            }
            
            let budget = 0;
            if (investment.slices && investment.slices.length > 0) {
              if (statusId !== null) {
                const slicesForStatus = investment.slices
                  .filter((s) => s.status === statusId)
                  .sort((a, b) => a.year - b.year);
                
                if (slicesForStatus.length > 0) {
                  budget = slicesForStatus.reduce((sum, s) => sum + Number(s.price || 0), 0);
                }
              }
              
              if (budget === 0) {
                const sortedSlices = investment.slices.sort((a, b) => b.year - a.year);
                if (sortedSlices.length > 0) {
                  budget = Number(sortedSlices[0].price || 0);
                }
              }
            }
            
            this.cachedUniqueInvestmentBudgets.set(investment.id, budget);
          }
        } catch (error) {
        }
      }
      
      this.cachedInvestmentIdsReady = true;
      } catch (error) {
        this.cachedInvestmentIdsReady = true;
    }
  }

  public previousLevel(event) {
    event.preventDefault()
    if (this.level > 1) {
      this.level -= 1;
    } else {
      this.ref.close();
    }
  }

  public sortSteps() {
    this.steps = this.steps.sort(
      (step1, step2) => {
        if (step1.order === step2.order) {
          return 0;
        } else if (step1.order > step2.order) {
          return 1;
        } {
          return -1;
        }
      }
    );
  }

  public setStepUp(step: KPI.WorkflowStep) {
    if (step.order > 1) {
      const index = this.steps.map(elt => elt.id).indexOf(step.id);
      if (index > 0) {
        const prevStep = this.steps[index - 1]
        step.order -= 1;
        prevStep.order += 1;
      }
      this.sortSteps();
    }
  }

  public setStepDown(step: KPI.WorkflowStep) {
    if (step.order < this.steps.length) {
      const index = this.steps.map(elt => elt.id).indexOf(step.id);
      if (index < (this.steps.length - 1)) {
        const nextStep = this.steps[index + 1];
        step.order += 1;
        nextStep.order -= 1;
      }
      this.sortSteps();
    }
  }

  public getSelectedTagIds(): number[] {
    const allTagIds: number[] = this.investmentTags.map(elt => elt.id)
    const selectedTagIds: number[] = [];
    for (const tagId of allTagIds) {
      const isSelected = this.isTagSelected(tagId);
      if (isSelected && this.getTotalTagProposedCount(tagId)) {
        selectedTagIds.push(tagId);
      }
    }
    return selectedTagIds;
  }

  public cantGoNextReason(): string {
    if (!this.roundName) {
      return this.translate.instant('pages.workflow.create-round.go-next-error-no-name');
    }
    if ((this.level === 1) && this.getSelectedTagIds().length === 0) {
      return this.translate.instant('pages.workflow.create-round.go-next-error-no-tags');
    }
    if ((this.level === 1) && this.getValidPerimeters().length === 0) {
      return this.translate.instant('pages.workflow.create-round.go-next-error-no-valid-sites');
    }
    return ''
  }


  public canGoNext(): boolean {
    return this.cantGoNextReason() === '';
  }

  public getValidPerimeters(): KPI.Perimeter[] {
    const validPerimeters: KPI.Perimeter[] = []
    let siteIds = this.sites.map(elt => elt.id);
    for (const perimeter of this.selectedSites) {
      const siteIndex = siteIds.indexOf(perimeter.id);
      if (siteIndex >= 0) {
        const site = this.sites[siteIndex];
        for (const tagId of this.getSelectedTagIds()) {
          if (this.workflowService.getSiteTagProposedAmount(site, tagId) > 0) {
            validPerimeters.push(perimeter);
            break;
          }
        }
      }
    }
    return validPerimeters
  }

  public createRound() {
    const selectedTagIds = this.getSelectedTagIds();
    const selectedPerimeters = this.getValidPerimeters();
    this.isCreating = true;
    this.workflowService.createRound(
      this.selectedPlan, this.roundName, selectedPerimeters, this.steps, selectedTagIds, this.projectCode || undefined
    ).subscribe(
      (creation) => {
        this.created.emit(creation);
        this.ref.close();
      },
      err => {
        this.errorMessage = err.message;
      }
    )
  }

  public exit(event) {
    event.preventDefault();
    this.ref.close();
  }

  public isTagSelected(tagId): boolean {
    return this.unselectedTags.indexOf(tagId) < 0;
  }

  public selectTag(tagId: number) {
    const index = this.unselectedTags.indexOf(tagId);
    if (index >= 0) {
      this.unselectedTags.splice(index, 1);
    } else {
      this.unselectedTags.push(tagId);
    }
  }

  public getTotalTagProposedAmount(tagId: number): number {
    const selectedIds = this.selectedSites.map(elt => elt.id);
    const sites = this.sites.filter(
      site => selectedIds.includes(site.id),
    );
    return this.workflowService.getTotalTagProposedAmount(sites, tagId);
  }

  public getTotalTagProposedCount(tagId: number): number {
    const selectedIds = this.selectedSites.map(elt => elt.id);
    const sites = this.sites.filter(
      (site) => selectedIds.includes(site.id),
    );
    return this.workflowService.getTotalTagProposedCount(sites, tagId);
  }

  protected readonly close = close;

  public isLastNoGoStep(step: KPI.WorkflowStep): boolean {
    return step.no_go_step && this.steps.filter((elt) => elt.no_go_step)?.length === 1;
  }

  public getProposedTotal(): Observable<string> {
    const selectedTagIds = this.getSelectedTagIds();
    const totalInvestment = selectedTagIds.reduce((total, tagId) => {
      const count = this.getTotalTagProposedCount(tagId);
      return total + count;
    }, 0);
    const totalPrice = selectedTagIds.reduce((total, tagId) => {
      const amount = this.getTotalTagProposedAmount(tagId);
      return total + amount;
    }, 0);
    const result = totalInvestment
      + ' '
      + this.translate.instant('pages.workflow.invests-for')
      + ' '
      + this.i18n.formatCurrency(totalPrice);
    this.proposedFromTagsDisplay = result;
    return new Observable(observer => {
      observer.next(result);
      observer.complete();
    });
  }

  public getProposedTotalDisplay(): string {
    const selectedTagIds = this.getSelectedTagIds();
    
    let totalInvestment: number;
    let totalPrice: number;
    
    if (this.cachedInvestmentIdsReady && this.cachedUniqueInvestmentIds.size > 0) {
      totalInvestment = this.cachedUniqueInvestmentIds.size;
      
      totalPrice = Array.from(this.cachedUniqueInvestmentBudgets.values()).reduce((sum, budget) => sum + budget, 0);
      
      if (this.cachedUniqueInvestmentBudgets.size === 0 || 
          (this.cachedUniqueInvestmentBudgets.size < this.cachedUniqueInvestmentIds.size && totalPrice === 0)) {
        totalPrice = selectedTagIds.reduce((total, tagId) => {
          const amount = this.getTotalTagProposedAmount(tagId);
          return total + amount;
        }, 0);
      }
    } else {
      totalInvestment = selectedTagIds.reduce((total, tagId) => {
        const count = this.getTotalTagProposedCount(tagId);
        return total + count;
      }, 0);
      totalPrice = selectedTagIds.reduce((total, tagId) => {
        const amount = this.getTotalTagProposedAmount(tagId);
        return total + amount;
      }, 0);
    }
    
    if (totalInvestment === 0) {
      return '';
    }
    
    const result = totalInvestment
      + ' '
      + this.translate.instant('pages.workflow.invests-for')
      + ' '
      + this.i18n.formatCurrency(totalPrice);
    return result;
  }

  private getRoundName() {
    const base = this.translate.instant('Validation');
    const now = new Date();
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const roundName = base + ' ' +`${day}/${month} - ${hours}:${minutes}`;

    return roundName
}

}
